/**
 * CadenceBuilder — /cadence/builder
 *
 * Manager tool for building and publishing configurable cadence programs.
 * Supports up to 10 steps with reorder (up/down), action type selection,
 * day offset, label, and optional custom script per step.
 *
 * Palette: Mi6 Black #0B0B0F · Red #DC2626 · Light bg #FAFAF7 · Slate text.
 * No per-role accent colors. No exclamation points. No emojis.
 */

import { useEffect, useState, useMemo } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { BackBar } from "@/components/BackBar";
import { useUnloadWarning } from "@/lib/useUnloadWarning";
import {
  ACTION_TYPE_LABELS,
  type CadenceActionType,
  type CadenceProgram,
  type ConfigurableCadenceStep,
  getActiveProgram,
  loadPrograms,
  publishProgram,
  savePrograms,
  setActiveProgram,
} from "@/lib/cadence-store";
import { getSession } from "@/lib/session";

// ─── Constants ───────────────────────────────────────────────────────────────

const MI6_BLACK = "#0B0B0F";
const MI6_RED = "#DC2626";
const MAX_STEPS = 10;

const ACTION_TYPES = Object.keys(ACTION_TYPE_LABELS) as CadenceActionType[];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function genId(): string {
  return `step_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function genProgramId(): string {
  return `prog_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function actionTypeIcon(t: CadenceActionType): string {
  switch (t) {
    case "email_opening":
    case "email_followup":
      return "Em";
    case "phone_no_vm":
    case "phone_with_vm":
      return "Ph";
    case "linkedin_dm":
      return "in";
    case "sms":
      return "sms";
    case "video_message":
      return "▶";
    case "direct_mail":
      return "⬚";
    case "decision_gate":
      return "◇";
    default:
      return "·";
  }
}

// ─── Step row ─────────────────────────────────────────────────────────────────

function StepRow({
  step,
  index,
  total,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}: {
  step: ConfigurableCadenceStep;
  index: number;
  total: number;
  onChange: (updated: ConfigurableCadenceStep) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const [scriptOpen, setScriptOpen] = useState(false);

  function update(partial: Partial<ConfigurableCadenceStep>) {
    onChange({ ...step, ...partial });
  }

  return (
    <div
      className="bg-white border border-neutral-200 rounded-xl overflow-hidden"
      style={{ borderLeft: `4px solid ${step.action_type === "decision_gate" ? MI6_RED : MI6_BLACK}` }}
    >
      {/* ── Main row ── */}
      <div className="flex items-start gap-3 px-4 py-3 flex-wrap sm:flex-nowrap">
        {/* Step number badge */}
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-sm flex-shrink-0 mt-0.5"
          style={{ background: step.action_type === "decision_gate" ? MI6_RED : MI6_BLACK }}
        >
          {step.position}
        </div>

        {/* Day offset */}
        <div className="flex flex-col gap-0.5 flex-shrink-0" style={{ minWidth: 72 }}>
          <label className="text-[9px] uppercase tracking-[0.18em] font-bold text-neutral-500">
            Day
          </label>
          <input
            type="number"
            min={0}
            max={365}
            value={step.day_offset}
            onChange={(e) => update({ day_offset: Math.max(0, parseInt(e.target.value) || 0) })}
            className="w-16 text-sm rounded-md border border-neutral-200 px-2 py-1.5 focus:outline-none focus:border-neutral-400 tabular-nums"
            style={{ color: MI6_BLACK }}
          />
        </div>

        {/* Action type */}
        <div className="flex flex-col gap-0.5 flex-shrink-0" style={{ minWidth: 200 }}>
          <label className="text-[9px] uppercase tracking-[0.18em] font-bold text-neutral-500">
            Action Type
          </label>
          <select
            value={step.action_type}
            onChange={(e) => update({ action_type: e.target.value as CadenceActionType })}
            className="text-sm rounded-md border border-neutral-200 px-2 py-1.5 bg-white focus:outline-none focus:border-neutral-400"
            style={{ color: MI6_BLACK }}
          >
            {ACTION_TYPES.map((t) => (
              <option key={t} value={t}>
                {ACTION_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        {/* Label */}
        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
          <label className="text-[9px] uppercase tracking-[0.18em] font-bold text-neutral-500">
            Label
          </label>
          <input
            type="text"
            value={step.label}
            onChange={(e) => update({ label: e.target.value })}
            className="w-full text-sm rounded-md border border-neutral-200 px-2.5 py-1.5 focus:outline-none focus:border-neutral-400"
            style={{ color: MI6_BLACK }}
            placeholder={`Cadence Step ${step.position} — ${ACTION_TYPE_LABELS[step.action_type]}`}
          />
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1 flex-shrink-0 mt-5">
          {/* Script toggle */}
          <button
            onClick={() => setScriptOpen(!scriptOpen)}
            title="Custom script"
            className="px-2 py-1.5 rounded-md text-[10px] uppercase tracking-wider font-bold border border-neutral-200 text-neutral-500 hover:text-neutral-700 hover:border-neutral-300 transition-colors"
          >
            Script
          </button>

          {/* Library placeholder */}
          <button
            title="Pick from template library (coming soon)"
            className="px-2 py-1.5 rounded-md text-[10px] uppercase tracking-wider font-bold border border-neutral-200 text-neutral-500 cursor-not-allowed"
            disabled
          >
            Library
          </button>

          {/* Reorder */}
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            title="Move up"
            className="w-9 h-9 md:w-7 md:h-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-700 disabled:opacity-30 transition-colors"
          >
            ▲
          </button>
          <button
            onClick={onMoveDown}
            disabled={index === total - 1}
            title="Move down"
            className="w-9 h-9 md:w-7 md:h-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-700 disabled:opacity-30 transition-colors"
          >
            ▼
          </button>

          {/* Delete */}
          <button
            onClick={onDelete}
            title="Delete step"
            aria-label="Delete step"
            className="w-9 h-9 md:w-7 md:h-7 rounded flex items-center justify-center text-neutral-300 hover:text-red-500 transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/></svg>
          </button>
        </div>
      </div>

      {/* ── Custom script (collapsible) ── */}
      {scriptOpen && (
        <div className="px-4 pb-3 pt-0 border-t border-neutral-100 space-y-1.5">
          <label className="block text-[9px] uppercase tracking-[0.18em] font-bold text-neutral-500 mt-2">
            Custom Script / Hint
          </label>
          <textarea
            value={step.custom_script ?? ""}
            onChange={(e) => update({ custom_script: e.target.value })}
            rows={3}
            placeholder="Enter talking points, script template, or coaching hint for this step…"
            className="w-full text-sm rounded-md border border-neutral-200 px-2.5 py-2 focus:outline-none focus:border-neutral-400 resize-none text-neutral-700"
          />
        </div>
      )}
    </div>
  );
}

// ─── Preview pane ─────────────────────────────────────────────────────────────

function PreviewPane({ program }: { program: CadenceProgram }) {
  const sorted = [...program.steps].sort((a, b) => a.position - b.position);

  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
      <div
        className="px-4 pt-3 pb-2 border-b border-neutral-100"
        style={{ borderLeft: `4px solid ${MI6_BLACK}` }}
      >
        <div
          className="text-[9px] uppercase tracking-[0.22em] font-bold"
          style={{ color: MI6_BLACK }}
        >
          Preview — Queue Appearance
        </div>
        <div className="text-xs text-neutral-500 mt-0.5">{program.name} · v{program.version}</div>
      </div>
      <div className="px-4 py-3 space-y-2">
        {sorted.length === 0 && (
          <div className="text-xs text-neutral-500 text-center py-4">No steps configured.</div>
        )}
        {sorted.map((s) => (
          <div key={s.id} className="flex items-start gap-2.5">
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0 mt-0.5"
              style={{ background: s.action_type === "decision_gate" ? MI6_RED : MI6_BLACK }}
            >
              {s.position}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-500">
                Day {s.day_offset} · {actionTypeIcon(s.action_type)} {ACTION_TYPE_LABELS[s.action_type]}
              </div>
              <div className="text-xs font-semibold mt-0.5" style={{ color: MI6_BLACK }}>
                {s.label || `Cadence Step ${s.position}`}
              </div>
              {s.custom_script && (
                <div className="text-[10px] text-neutral-500 italic mt-0.5 line-clamp-2">
                  {s.custom_script}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Program selector ─────────────────────────────────────────────────────────

function ProgramSelector({
  programs,
  activeId,
  onSelect,
  onNew,
}: {
  programs: CadenceProgram[];
  activeId: string;
  onSelect: (id: string) => void;
  onNew: () => void;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <label className="text-[9px] uppercase tracking-[0.18em] font-bold text-neutral-500">
        Program
      </label>
      <select
        value={activeId}
        onChange={(e) => onSelect(e.target.value)}
        className="text-sm rounded-md border border-neutral-200 px-2.5 py-1.5 bg-white focus:outline-none focus:border-neutral-400"
        style={{ color: MI6_BLACK }}
      >
        {programs.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} {p.is_default ? "(Default)" : ""}
          </option>
        ))}
      </select>
      <button
        onClick={onNew}
        className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider border border-neutral-200 text-neutral-600 hover:border-neutral-400 hover:text-neutral-800 transition-colors"
      >
        + New Program
      </button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CadenceBuilder() {
  const [programs, setPrograms] = useState<CadenceProgram[]>([]);
  const [editingId, setEditingId] = useState<string>("");
  const [draft, setDraft] = useState<CadenceProgram | null>(null);
  const [saved, setSaved] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Warn the user before they leave with unpublished cadence edits.
  useUnloadWarning(
    !!draft && !saved,
    "This cadence program has unpublished changes. Click Save & Publish before leaving."
  );

  // Load on mount
  useEffect(() => {
    const loaded = loadPrograms();
    setPrograms(loaded);
    const active = getActiveProgram();
    setEditingId(active.id);
    // Deep-clone to avoid mutating localStorage-backed objects
    setDraft(JSON.parse(JSON.stringify(active)));
    setSaved(true);
  }, []);

  // Switch program
  function handleSelectProgram(id: string) {
    setEditingId(id);
    const found = programs.find((p) => p.id === id);
    if (found) setDraft(JSON.parse(JSON.stringify(found)));
    setSaved(true);
  }

  // Create new blank program
  function handleNewProgram() {
    // Programs are owned by the signed-in user (uuid). Pre-hydration
    // fallback keeps the legacy literal so a half-loaded page can still
    // create a draft; publishProgram() re-validates owner_id on save.
    const liveOwner = getSession()?.id ?? "current_user";
    const newProg: CadenceProgram = {
      id: genProgramId(),
      name: "New Cadence Program",
      owner_id: liveOwner,
      steps: [],
      version: 0,
      published_at: new Date().toISOString().slice(0, 10),
      is_default: false,
    };
    const updated = [...programs, newProg];
    setPrograms(updated);
    savePrograms(updated);
    setEditingId(newProg.id);
    setDraft(JSON.parse(JSON.stringify(newProg)));
    setSaved(false);
  }

  // Add step
  function handleAddStep() {
    if (!draft) return;
    if (draft.steps.length >= MAX_STEPS) return;
    const maxPos = draft.steps.reduce((m, s) => Math.max(m, s.position), 0);
    const maxDay = draft.steps.reduce((m, s) => Math.max(m, s.day_offset), 0);
    const newStep: ConfigurableCadenceStep = {
      id: genId(),
      position: maxPos + 1,
      day_offset: maxDay + 2,
      action_type: "email_followup",
      label: `Cadence Step ${maxPos + 1}`,
    };
    setDraft({ ...draft, steps: [...draft.steps, newStep] });
    setSaved(false);
  }

  // Update step
  function handleStepChange(stepId: string, updated: ConfigurableCadenceStep) {
    if (!draft) return;
    setDraft({
      ...draft,
      steps: draft.steps.map((s) => (s.id === stepId ? updated : s)),
    });
    setSaved(false);
  }

  // Delete step
  function handleDeleteStep(stepId: string) {
    if (!draft) return;
    const remaining = draft.steps
      .filter((s) => s.id !== stepId)
      .map((s, i) => ({ ...s, position: i + 1 }));
    setDraft({ ...draft, steps: remaining });
    setSaved(false);
  }

  // Reorder
  function handleMove(stepId: string, direction: "up" | "down") {
    if (!draft) return;
    const sorted = [...draft.steps].sort((a, b) => a.position - b.position);
    const idx = sorted.findIndex((s) => s.id === stepId);
    if (idx < 0) return;
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;
    // Swap positions
    const aPos = sorted[idx].position;
    const bPos = sorted[targetIdx].position;
    sorted[idx] = { ...sorted[idx], position: bPos };
    sorted[targetIdx] = { ...sorted[targetIdx], position: aPos };
    setDraft({ ...draft, steps: sorted });
    setSaved(false);
  }

  // Save & Publish
  function handlePublish() {
    if (!draft) return;
    setPublishing(true);
    try {
      const published = publishProgram(draft);
      setActiveProgram(published.id);
      const refreshed = loadPrograms();
      setPrograms(refreshed);
      setDraft(JSON.parse(JSON.stringify(published)));
      setSaved(true);
    } finally {
      setPublishing(false);
    }
  }

  // Update name
  function handleNameChange(name: string) {
    if (!draft) return;
    setDraft({ ...draft, name });
    setSaved(false);
  }

  const sortedSteps = useMemo(
    () => (draft ? [...draft.steps].sort((a, b) => a.position - b.position) : []),
    [draft]
  );

  const isDefault = draft?.id === "default_mi6";
  const stepCount = draft?.steps.length ?? 0;

  if (!draft) {
    return (
      <div className="min-h-screen" style={{ background: "#FAFAF7" }}>
        <div className="max-w-5xl mx-auto px-4 py-12 text-center text-neutral-500 text-sm">
          Loading cadence programs…
        </div>
      </div>
    );
  }

  return (
    <OperatorShell breadcrumbs={[{ label: "Cadence Builder" }]} pageTitle="Cadence Builder" pageMeta="Cadence programs">
      <BackBar href="/dashboards/management" label="Management Dashboard" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* ── Page header ── */}
        <div
          className="rounded-xl p-5 text-white mb-6"
          style={{ background: `linear-gradient(135deg, ${MI6_BLACK}, #1F2937)` }}
        >
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">
                Mi6 Attilius · Cadence Engine · Manager Tools
              </div>
              <h1 className="text-2xl font-bold">Cadence Builder</h1>
              <div className="text-neutral-500 text-sm mt-0.5">
                Configure steps, day offsets, action types, and scripts for your team's cadence programs.
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-right">
                <div className="text-2xl font-bold tabular-nums">{stepCount}</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                  Steps
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold tabular-nums">v{draft.version}</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                  Version
                </div>
              </div>
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all hover:shadow-md active:scale-95 disabled:opacity-60"
                style={{ background: MI6_RED }}
              >
                {publishing ? "Publishing…" : "Save and Publish"}
              </button>
            </div>
          </div>

          {saved && (
            <div
              className="mt-3 text-[11px] font-bold uppercase tracking-wider text-green-300"
            >
              Published · v{draft.version} is now live
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* ── Left: editor ── */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Program selector + metadata */}
            <div className="bg-white rounded-xl border border-neutral-200 shadow-sm px-5 py-4 space-y-4">
              <ProgramSelector
                programs={programs}
                activeId={editingId}
                onSelect={handleSelectProgram}
                onNew={handleNewProgram}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Name */}
                <div>
                  <label className="block text-[9px] uppercase tracking-[0.18em] font-bold text-neutral-500 mb-1">
                    Program Name
                  </label>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    disabled={isDefault}
                    className="w-full text-sm rounded-md border border-neutral-200 px-2.5 py-2 focus:outline-none focus:border-neutral-400 disabled:bg-neutral-50 disabled:text-neutral-500"
                    style={{ color: MI6_BLACK }}
                    placeholder="Program name"
                  />
                  {isDefault && (
                    <div className="text-[9px] text-neutral-500 mt-0.5">
                      Default program name is locked. Clone to create a custom version.
                    </div>
                  )}
                </div>

                {/* Published date + status */}
                <div className="flex flex-col gap-2">
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-neutral-500 mb-1">
                      Published
                    </div>
                    <div className="text-sm text-neutral-700 font-medium">{draft.published_at}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center text-[9px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded"
                      style={
                        draft.is_default
                          ? { background: "#0B0B0F", color: "#FAFAF7" }
                          : { background: "#EFEFEE", color: "#525258", border: "1px solid #E5E5E7" }
                      }
                    >
                      {draft.is_default ? "Default" : "Custom"}
                    </span>
                    <span
                      className="inline-flex items-center text-[9px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded"
                      style={{ background: "#DCFCE7", color: "#166534" }}
                    >
                      v{draft.version}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Steps section header */}
            <div className="flex items-center justify-between">
              <div>
                <div
                  className="text-[10px] uppercase tracking-[0.22em] font-bold"
                  style={{ color: MI6_BLACK }}
                >
                  Cadence Steps
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {stepCount} of {MAX_STEPS} steps configured
                </div>
              </div>
            </div>

            {/* Steps list */}
            <div className="space-y-3">
              {sortedSteps.length === 0 && (
                <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center text-neutral-500 text-sm">
                  No steps yet. Add your first cadence step below.
                </div>
              )}
              {sortedSteps.map((step, i) => (
                <StepRow
                  key={step.id}
                  step={step}
                  index={i}
                  total={sortedSteps.length}
                  onChange={(updated) => handleStepChange(step.id, updated)}
                  onDelete={() => handleDeleteStep(step.id)}
                  onMoveUp={() => handleMove(step.id, "up")}
                  onMoveDown={() => handleMove(step.id, "down")}
                />
              ))}
            </div>

            {/* Add step button */}
            <button
              onClick={handleAddStep}
              disabled={stepCount >= MAX_STEPS}
              className="w-full py-3 rounded-xl border-2 border-dashed border-neutral-200 text-xs font-bold uppercase tracking-wider text-neutral-500 hover:border-neutral-400 hover:text-neutral-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {stepCount >= MAX_STEPS
                ? `Maximum ${MAX_STEPS} cadence steps reached`
                : "+ Add Cadence Step"}
            </button>

            {/* Publish footer shortcut */}
            <div className="flex justify-end pt-2 pb-4">
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="px-6 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all hover:shadow-md active:scale-95 disabled:opacity-60"
                style={{ background: MI6_RED }}
              >
                {publishing ? "Publishing…" : "Save and Publish"}
              </button>
            </div>
          </div>

          {/* ── Right: preview (sticky on lg+) ── */}
          <div className="w-full lg:w-72 xl:w-80 flex-shrink-0">
            <div className="lg:sticky lg:top-20">
              <PreviewPane program={draft} />

              {/* Tips */}
              <div className="mt-4 bg-white rounded-xl border border-neutral-200 shadow-sm px-4 py-4 space-y-2">
                <div
                  className="text-[9px] uppercase tracking-[0.22em] font-bold"
                  style={{ color: MI6_BLACK }}
                >
                  Builder Notes
                </div>
                <ul className="text-xs text-neutral-500 space-y-1.5 list-disc list-inside">
                  <li>Up to 10 cadence steps per program.</li>
                  <li>Day offsets are relative to prospect enrollment date (Day 0).</li>
                  <li>Pursuit Decision steps resolve the cadence — place last.</li>
                  <li>Publishing migrates all active prospects to the new step schedule.</li>
                  <li>Template library integration is planned for a future release.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </OperatorShell>
  );
}
